import api from './client';
import { getItemImage } from '@/shared/utils/itemImages';
import { definirAccessToken, limparSessao, obterAccessToken, temSessao } from './session';
import {
  candidato,
  claDaLista,
  claDetalhado,
  competicao,
  eventoAtivo,
  funcionarioDoNegocio,
  itemDoCatalogo,
  itemPremium,
  mensagemDoCla,
  mercadoDoDia,
  missaoDoDia,
  pecaDoEquipamento,
  peixeDoInventario,
  perfilParaPlayerState,
  perfilPublico,
  posicaoDaCompeticao,
  posicaoDeCla,
  posicaoDeJogador,
  premioDaRoleta,
  resultadoDaPescaria,
} from './adapters';

/**
 * As chamadas ao backend V2.
 *
 * Toda a tradução de forma acontece aqui e em `adapters.js` — quem chama recebe
 * o que as stores já sabem consumir. É a única camada que conhece as rotas
 * novas.
 */

/**
 * Chave de idempotência para mutação econômica.
 *
 * O backend exige o header em `sell` e `shop/buy`: a rede erra e o dedo clica
 * duas vezes, e sem a chave a segunda entrega venderia peixe que já não existe —
 * ou, se as duas chegassem juntas, creditaria o ouro duas vezes.
 *
 * `randomUUID` não existe em contexto sem HTTPS em navegador antigo; o fallback
 * não precisa ser criptográfico, só irrepetível dentro da sessão.
 */
function chaveIdempotente() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/* ------------------------------------------------------------------ sessão */

export async function entrar({ identifier, password }) {
  const { data } = await api.post('/api/auth/login', { identifier, password });
  definirAccessToken(data.accessToken);
  return data;
}

export async function registrar({ username, email, password, indication }) {
  const { data } = await api.post('/api/auth/register', {
    username,
    email,
    password,
    // O schema do backend é `.strict()`: mandar `indication: undefined` é
    // diferente de não mandar o campo, e um corpo com chave não declarada é
    // rejeitado, não ignorado.
    ...(indication ? { indication } : {}),
  });
  definirAccessToken(data.accessToken);
  return data;
}

export async function sair() {
  try {
    await api.post('/api/auth/logout');
  } finally {
    // A sessão local cai mesmo se o servidor não responder: manter um token que
    // o jogador pediu para descartar é pior do que a falha do logout remoto.
    limparSessao();
  }
}

/** Quem está autenticado, com o `playerId` que endereça as rotas de jogo. */
export async function quemSouEu() {
  const { data } = await api.get('/api/auth/me');
  return data;
}

/* -------------------------------------------------------------------- jogo */

export async function perfil(playerId) {
  const { data } = await api.get(`/api/player/${playerId}`);
  return data;
}

/**
 * O inventário de peixes, paginado por cursor.
 *
 * `limite` alto de propósito: a tela do inventário mostra tudo de uma vez e o
 * backend tem teto próprio. Se vier `temMais`, quem chama decide se busca o
 * resto — não se busca em laço aqui para não transformar um inventário grande em
 * dez requisições silenciosas.
 */
export async function inventario(playerId, { cursor, limite = 100 } = {}) {
  const { data } = await api.get(`/api/player/${playerId}/inventory`, {
    params: { ...(cursor ? { cursor } : {}), limite },
  });
  return {
    fish: (data.itens || []).map(peixeDoInventario),
    proximoCursor: data.proximoCursor,
    temMais: data.temMais,
  };
}

/**
 * As iscas do jogador (`GET /player/:id/baits`).
 *
 * A rota nasceu em 02/09/2026: a compra gravava em `player_items` e nenhuma
 * leitura devolvia a contagem — `inventory.baits` ficava vazio para sempre e o
 * `canFish` travava o botão de pescar. `id` é o id do **catálogo** (`baits.id`),
 * que é o que `pescar` aceita em `baitId`. `fishingPower` porque é o campo que
 * `FishingAction` ordena para escolher a melhor isca.
 */
export async function iscasDoJogador(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/baits`);
  return (data.itens || []).map((isca) => ({
    id: isca.id,
    name: isca.nome,
    fishingPower: isca.poder,
    quantity: isca.quantidade,
    image: getItemImage(isca.nome),
  }));
}

/**
 * O baú do jogador: vara, barco e casa, equipados ou não, já separados nas
 * três listas que o inventário do cliente sempre teve.
 */
export async function equipamentos(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/gear`);
  const pecas = (data.itens || []).map(pecaDoEquipamento);
  return {
    rods: pecas.filter((p) => p.category === 'rods'),
    boats: pecas.filter((p) => p.category === 'boats'),
    houses: pecas.filter((p) => p.category === 'houses'),
  };
}

/**
 * Equipar uma peça. `gearId` é o id da **instância** (`player_gear.id`), não o
 * do catálogo — ver `pecaDoEquipamento`. A resposta é o perfil inteiro, com os
 * atributos já recalculados pelo servidor: quem chama aplica direto em
 * `SET_PLAYER_STATE`, sem segunda ida.
 */
export async function equipar(playerId, gearId) {
  const { data } = await api.post(`/api/player/${playerId}/gear/equip`, { gearId });
  return perfilParaPlayerState(data, playerId);
}

/** Desequipar um slot: `rod`, `boat` ou `house`. Devolve o perfil, como equipar. */
export async function desequipar(playerId, slot) {
  const { data } = await api.post(`/api/player/${playerId}/gear/unequip`, { slot });
  return perfilParaPlayerState(data, playerId);
}

/** Os mapas liberados. Devolve os **ids** — `purchasedMaps` passou a ser de ids. */
export async function mapasLiberados(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/maps`);
  return (data.itens || []).map((m) => m.id);
}

/**
 * Liberar (comprar) um mapa. O preço vem de `maps.gold` no servidor; os
 * requisitos — nível do jogador e nível do barco equipado — também são
 * conferidos lá. Mutação econômica: vai com chave de idempotência.
 */
export async function liberarMapa(playerId, mapId) {
  const { data } = await api.post(
    `/api/player/${playerId}/maps/unlock`,
    { mapId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { mapId: data.mapId, name: data.nome, goldSpent: data.goldGasto, gold: data.goldRestante };
}

/** Trocar o mapa atual. Por **id**, não por nome — `current_map_id` é FK. */
export async function trocarMapa(playerId, mapId) {
  const { data } = await api.post(`/api/player/${playerId}/map`, { mapId });
  return { id: data.mapaAtual.id, name: data.mapaAtual.nome };
}

/**
 * Níveis e custos de upgrade, numa resposta só — o Strapi exigia nove
 * requisições (uma de níveis, oito de custo). Devolve os dois dicionários que a
 * store de upgrade sempre guardou.
 */
export async function upgrades(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/upgrades`);
  const stats = {};
  const costs = {};
  for (const a of data.atributos || []) {
    stats[a.atributo] = a.nivel;
    costs[a.atributo] = a.custo;
  }
  return { stats, costs, gold: data.gold };
}

/** Aplicar um upgrade. Só o atributo: o custo é do servidor. Idempotente. */
export async function aplicarUpgrade(playerId, atributo) {
  const { data } = await api.post(
    `/api/player/${playerId}/upgrades`,
    { atributo },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    stat: data.atributo,
    level: data.nivel,
    cost: data.custoPago,
    gold: data.goldRestante,
    nextCost: data.proximoCusto,
  };
}

/** Ranking de jogadores. `tipo` é um de `fishers`, `levels`, `richest`. Público. */
export async function rankingDeJogadores(tipo) {
  const { data } = await api.get(`/api/rankings/players/${tipo}`);
  return (data.posicoes || []).map(posicaoDeJogador);
}

/** Ranking de clãs, pela pontuação apurada de madrugada pelo worker. Público. */
export async function rankingDeClas() {
  const { data } = await api.get('/api/rankings/clans');
  return (data.posicoes || []).map(posicaoDeCla);
}

/**
 * Perfil, inventário, baú e mapas liberados juntos, na forma que
 * `SET_PLAYER_STATE` espera.
 *
 * As chamadas vão em paralelo porque não dependem uma da outra. Era uma
 * chamada só no Strapi, com `populate` de sete relações — que é exatamente o que
 * derrubava o servidor.
 */
export async function carregarJogador(playerId) {
  const [dadosDoPerfil, dadosDoInventario, bau, mapas, premium, iscas] = await Promise.all([
    perfil(playerId),
    inventario(playerId),
    equipamentos(playerId),
    mapasLiberados(playerId),
    inventarioPremium(playerId),
    iscasDoJogador(playerId),
  ]);
  const estado = perfilParaPlayerState(dadosDoPerfil, playerId);
  estado.inventory.fish = dadosDoInventario.fish;
  estado.inventory.rods = bau.rods;
  estado.inventory.boats = bau.boats;
  estado.inventory.houses = bau.houses;
  estado.inventory.baits = iscas;
  // Tônicos e baús comprados: a aba "outros" do inventário. `getItemImage`
  // resolve a arte pelo nome, como em todo o resto.
  estado.inventory.others = premium.map((item) => ({
    ...item,
    image: getItemImage(item.name),
  }));
  estado.purchasedMaps = mapas;
  return estado;
}

/**
 * Pescar.
 *
 * O corpo leva **só** a isca, e nem sempre. Peixe, peso, raridade e valor são
 * decididos no servidor — no Strapi o cliente mandava o peixe já pescado, e
 * qualquer um com o DevTools aberto declarava um lendário de 50 kg.
 *
 * O mapa também não vai: quem sabe onde o jogador está é `players.current_map_id`.
 */
export async function pescar(playerId, baitId) {
  const { data } = await api.post(
    `/api/player/${playerId}/fish`,
    baitId ? { baitId } : {},
  );
  return resultadoDaPescaria(data);
}

/** Vender peixes. Só os ids: o preço vem do servidor. */
export async function vender(playerId, idsDosPeixes) {
  const { data } = await api.post(
    `/api/player/${playerId}/sell`,
    { peixes: idsDosPeixes },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { sold: data.vendidos, goldGained: data.goldGanho, gold: data.goldTotal };
}

/** Comprar. O corpo diz **o que**, nunca **por quanto**. */
export async function comprar(playerId, { categoria, itemId, quantidade = 1 }) {
  const { data } = await api.post(
    `/api/player/${playerId}/shop/buy`,
    { categoria, itemId, quantidade },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    category: data.categoria,
    itemId: data.itemId,
    quantity: data.quantidade,
    goldSpent: data.goldGasto,
    gold: data.goldRestante,
  };
}

/** A vitrine. `categoria` é uma de rod, boat, bait, house, map. */
export async function catalogo(categoria) {
  const { data } = await api.get(`/api/catalogo/${categoria}`);
  return data.map(itemDoCatalogo);
}

/* ---------------------------------------------------------------- missões */

/**
 * As missões do dia. O GET **atribui** as três missões na primeira chamada do
 * dia — chame antes de resgatar. Progresso vem absoluto; o adapter põe o `%`.
 */
export async function missoes(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/missions`);
  return (data || []).map(missaoDoDia);
}

/**
 * O que as missões de hoje pagam, antes do resgate: o tier (1–5) e o prêmio. O tier é o mesmo que
 * decidiu quantas missões o dia tem e de que tamanho — dia difícil é o dia que paga mais.
 */
export async function premioDasMissoes(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/missions/reward`);
  // `moeda` vem no singular: 'gold' | 'parallelium'.
  return { tier: data.tier, type: data.moeda, amount: data.valor };
}

/** Resgata a recompensa do dia de missões. Tudo ou nada, decidido no servidor. */
export async function resgatarMissoes(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/missions/claim`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  // `moeda` vem no singular do catálogo: 'gold' | 'parallelium'.
  return { type: data.moeda, amount: data.valor };
}

/* ------------------------------------------------------------- conquistas */

/** As conquistas com progresso. Cru do V2: quem filtra é a store. */
export async function conquistas(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/achievements`);
  return data || [];
}

export async function resgatarConquista(playerId, rewardId) {
  const { data } = await api.post(
    `/api/player/${playerId}/achievements/${rewardId}/claim`,
    {},
  );
  return { staminaBonus: data.bonusDeEnergia };
}

/* -------------------------------------------------------- recompensa diária */

/** Resgata a recompensa de login do dia. 409 = já resgatada hoje. */
export async function resgatarDiaria(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/daily-reward/claim`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  // `tickets`: o +1 da diária, que alimenta a roleta. Voltou em 05/09/2026.
  return { day: data.dia, type: data.tipo, amount: data.valor, tickets: data.tickets ?? 0 };
}

/* ----------------------------------------------------------------- roleta */

/** Os segmentos da roda — a mesma lista que o sorteio usa. */
export async function premiosDaRoleta(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/roulette/prizes`);
  return (data || []).map(premioDaRoleta);
}

/**
 * Gira. O prêmio já é **creditado aqui** — não existe mais o passo de `claim`
 * do Strapi; a animação do cliente é só teatro sobre um resultado fechado.
 */
export async function girarRoleta(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/roulette/spin`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { id: data.id, name: data.premio, type: data.tipo, value: data.valor };
}

/* ---------------------------------------------------------------- premium */

/** O catálogo premium. Público no V2; o adapter traduz para a vitrine. */
export async function itensPremium() {
  const { data } = await api.get('/api/premium-items');
  return (data || []).map(itemPremium);
}

export async function comprarPremium(playerId, itemId) {
  const { data } = await api.post(
    `/api/player/${playerId}/premium/${itemId}/purchase`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    itemId: data.itemId,
    name: data.nome,
    type: data.categoria,
    quantity: data.quantidade,
    paralleliumsSpent: data.paralleliumsGastos,
  };
}

/** Usa um tônico. `:itemId` é o id do **catálogo** premium, não de inventário. */
export async function usarTonico(playerId, itemId) {
  const { data } = await api.post(
    `/api/player/${playerId}/premium/tonic/${itemId}/use`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { effect: data.efeito, value: data.valor, expiresAt: data.expiraEm };
}

export async function abrirBau(playerId, itemId) {
  const { data } = await api.post(
    `/api/player/${playerId}/premium/lootbox/${itemId}/open`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { box: data.bau, reward: { name: data.premio, type: data.tipo, amount: data.valor } };
}

/** O inventário premium (tônicos, baús...) do jogador. */
export async function inventarioPremium(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/premium/inventory`);
  return (data || []).map((item) => ({
    id: item.itemId,
    name: item.nome,
    type: item.categoria,
    tooltipText: item.tooltip || '',
    effectType: item.tipoDeEfeito,
    effectNumber: item.numeroDoEfeito,
    quantity: item.quantidade,
  }));
}

/* ------------------------------------------------------------- pagamentos */

/** Os pacotes de parallelium, com preço em **centavos**. */
export async function pacotesDePagamento(locale) {
  const { data } = await api.get('/api/payments/packages', {
    params: locale ? { locale } : {},
  });
  return data || [];
}

/**
 * Abre o checkout da Stripe. A resposta é a URL para onde navegar; quem
 * credita depois é o **webhook** — não existe mais o `confirm` do Strapi, o
 * cliente confirma consultando o histórico.
 */
export async function checkout(playerId, { packageId, locale, paymentMethod }) {
  const { data } = await api.post(
    `/api/player/${playerId}/payments/checkout`,
    {
      packageId,
      ...(locale ? { locale } : {}),
      ...(paymentMethod ? { paymentMethod } : {}),
    },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { sessionId: data.sessionId, url: data.url };
}

export async function historicoDePagamentos(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/payments/history`);
  return data || [];
}

/* ------------------------------------------------------------ competições */

export async function competicoes() {
  const { data } = await api.get('/api/competitions/active');
  return (data || []).map(competicao);
}

export async function rankingDaCompeticao(competitionId) {
  const { data } = await api.get(`/api/competitions/${competitionId}/ranking`);
  return (data.posicoes || []).map(posicaoDaCompeticao);
}

export async function inscreverNaCompeticao(playerId, competitionId) {
  await api.post(`/api/player/${playerId}/competitions/${competitionId}/join`, {});
}

/* --------------------------------------------------------------------- clã */

export async function clas({ cursor, limite } = {}) {
  const { data } = await api.get('/api/clans', {
    params: { ...(cursor ? { cursor } : {}), ...(limite ? { limite } : {}) },
  });
  return {
    itens: (data.itens || []).map(claDaLista),
    proximoCursor: data.proximoCursor,
    temMais: data.temMais,
  };
}

/**
 * Funda um clã. Custa 10.000 de gold, cobrados no servidor.
 *
 * `pais` é o código da folha de bandeiras (`br`, `gb-eng`); sem ele o clã nasce "sem país".
 */
export async function criarCla(playerId, { nome, descricao, tipo, pais }) {
  const { data } = await api.post(
    `/api/player/${playerId}/clan`,
    {
      nome,
      ...(descricao ? { descricao } : {}),
      ...(tipo ? { tipo } : {}),
      ...(pais ? { pais } : {}),
    },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** O líder troca o país do clã; `null` volta a "sem país". Não é a bandeira — essa é `escolherBandeira`. */
export async function trocarPaisDoCla(playerId, pais) {
  const { data } = await api.put(`/api/player/${playerId}/clan/country`, { pais });
  return data;
}

export async function entrarNoCla(playerId, clanId) {
  const { data } = await api.post(`/api/player/${playerId}/clan/${clanId}/join`, {});
  return data;
}

export async function sairDoCla(playerId) {
  await api.post(`/api/player/${playerId}/clan/leave`, {});
}

export async function detalhesDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/details`);
  return claDetalhado(data);
}

export async function contribuirParaOCla(playerId, quantidade) {
  const { data } = await api.post(
    `/api/player/${playerId}/clan/contribute`,
    { quantidade },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    amount: data.quantidade,
    gold: data.goldRestante,
    clanBalance: data.saldoDoCla,
    // 1 moeda de cla a cada 100 de gold — creditada pelo servidor (03/09/2026).
    clanCoins: data.moedasRecebidas ?? 0,
  };
}

export async function convidarParaOCla(playerId, username) {
  await api.post(`/api/player/${playerId}/clan/invites`, { username });
}

export async function convitesDeCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/invites`);
  return (data.itens || []).map((c) => ({
    id: c.id,
    clanId: c.clanId,
    clanName: c.cla,
    createdAt: c.criadoEm,
  }));
}

export async function aceitarConvite(playerId, inviteId) {
  await api.post(`/api/player/${playerId}/invites/${inviteId}/accept`, {});
}

export async function rejeitarConvite(playerId, inviteId) {
  await api.post(`/api/player/${playerId}/invites/${inviteId}/reject`, {});
}

export async function expulsarDoCla(playerId, membroId) {
  await api.post(`/api/player/${playerId}/clan/kick`, { membroId });
}

export async function transferirLideranca(playerId, novoLiderId) {
  await api.post(`/api/player/${playerId}/clan/transfer`, { novoLiderId });
}

export async function dissolverCla(playerId) {
  await api.delete(`/api/player/${playerId}/clan`);
}

export async function chatDoCla(playerId, { cursor, limite = 50 } = {}) {
  const { data } = await api.get(`/api/player/${playerId}/clan/chat`, {
    params: { ...(cursor ? { cursor } : {}), limite },
  });
  return {
    mensagens: (data.mensagens || []).map(mensagemDoCla),
    proximoCursor: data.proximoCursor,
    temMais: data.temMais,
  };
}

export async function enviarMensagemDoCla(playerId, texto) {
  const { data } = await api.post(`/api/player/${playerId}/clan/chat`, { texto });
  return { id: data.id };
}

export async function apagarMensagemDoCla(playerId, messageId) {
  await api.delete(`/api/player/${playerId}/clan/chat/${messageId}`);
}

/** Sobe um nível do boost, pago pelo **caixa do clã**. Líder apenas. */
export async function melhorarBoost(playerId, tipo) {
  const { data } = await api.post(`/api/player/${playerId}/clan/boosts/${tipo}/upgrade`, {});
  return data;
}

export async function ativarBoost(playerId, tipo) {
  const { data } = await api.post(`/api/player/${playerId}/clan/boosts/${tipo}/activate`, {});
  return data;
}

/* ------------------------------------------------- o clã da 1.1 (D22/D24/D25) */

/**
 * O mural de avisos — o quadro do Ikariam.
 *
 * Duas zonas com vidas diferentes numa resposta só: o texto do alto (regras e
 * boas-vindas) e os avisos de baixo. `souOLider` vem junto **para desenhar o
 * botão**, nunca para autorizar — quem autoriza é o servidor, a cada escrita.
 */
export async function muralDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/board`);
  return data;
}

export async function salvarTextoDoMural(playerId, texto) {
  const { data } = await api.put(`/api/player/${playerId}/clan/board/text`, { texto });
  return data;
}

export async function publicarAviso(playerId, { titulo, corpo, fixado }) {
  const { data } = await api.post(`/api/player/${playerId}/clan/board/announcements`, {
    titulo,
    corpo,
    ...(fixado === undefined ? {} : { fixado }),
  });
  return data;
}

export async function editarAviso(playerId, avisoId, mudancas) {
  const { data } = await api.patch(
    `/api/player/${playerId}/clan/board/announcements/${avisoId}`,
    mudancas,
  );
  return data;
}

export async function removerAviso(playerId, avisoId) {
  await api.delete(`/api/player/${playerId}/clan/board/announcements/${avisoId}`);
}

/**
 * O quadro: o movimento do clã.
 *
 * Não confundir com o mural — este é escrito pelo sistema e diz o que o clã
 * **fez**; aquele é escrito pelo líder e diz o que ele quer que se saiba.
 */
export async function quadroDoCla(playerId, limite) {
  const { data } = await api.get(`/api/player/${playerId}/clan/feed`, {
    params: limite ? { limite } : {},
  });
  return data;
}

/** Quem deu quanto — o crédito visual que o problema 4 do D22 pedia. */
export async function contribuintesDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/contributors`);
  return data;
}

/* ------------------------------------------------------ a bandeira (D25) */

export async function bandeirasProntas(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/flags`);
  return data;
}

export async function bandeiraDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/flag`);
  return data;
}

export async function escolherBandeira(playerId, bandeiraId) {
  const { data } = await api.put(`/api/player/${playerId}/clan/flag`, { bandeiraId });
  return data;
}

/**
 * Envia uma bandeira própria.
 *
 * `FormData` sem `Content-Type` declarado: o navegador monta o cabeçalho com o
 * `boundary` que só ele conhece. Defini-lo à mão — o que o axios faria se
 * herdasse o padrão do cliente — produz um corpo que o servidor não consegue
 * separar em partes.
 */
export async function enviarBandeira(playerId, arquivo) {
  const formulario = new FormData();
  formulario.append('arquivo', arquivo);
  const { data } = await api.post(`/api/player/${playerId}/clan/flag`, formulario, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export async function removerBandeira(playerId) {
  const { data } = await api.delete(`/api/player/${playerId}/clan/flag`);
  return data;
}

/** Denuncia a bandeira de um clã qualquer — inclusive um que você não integra. */
export async function denunciarBandeira(playerId, clanId, { motivo, detalhe }) {
  await api.post(`/api/player/${playerId}/clan/flags/${clanId}/report`, {
    motivo,
    ...(detalhe ? { detalhe } : {}),
  });
}

/* ------------------------------------------------------- a loja do clã (D24) */

export async function lojaDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/shop`);
  return data;
}

/** O que dá para estocar, com o custo em caixa. Só o líder. */
export async function reposicaoDaLoja(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/shop/restock`);
  return data;
}

export async function reporNaLoja(playerId, itemId, unidades) {
  const { data } = await api.post(
    `/api/player/${playerId}/clan/shop/restock`,
    { itemId, unidades },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function comprarNaLojaDoCla(playerId, itemId) {
  const { data } = await api.post(
    `/api/player/${playerId}/clan/shop/buy`,
    { itemId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/* ------------------------------------------- meta da semana e pedidos (1.1) */

export async function metaDaSemana(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/goal`);
  return data;
}

export async function pedidosDoCla(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/requests`);
  return data;
}

/**
 * O que se pode pedir: os seis catálogos, numa chamada.
 *
 * Buscado uma vez, ao abrir o formulário — a lista de pedidos muda a cada
 * doação e é relida o tempo todo; o catálogo muda quando o jogo muda.
 */
export async function catalogoDePedido(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/clan/requests/catalog`);
  return data;
}

/**
 * Abre um pedido com de uma a cinco coisas.
 *
 * `itens` é `[{ tipo, alvoId, quantidade }]`. `alvoId` fica de fora nas duas
 * moedas — ouro e Parallelium são valor puro, e o servidor recusa um id ali.
 */
export async function pedirApetrecho(playerId, itens) {
  const { data } = await api.post(`/api/player/${playerId}/clan/requests`, { itens });
  return data;
}

/**
 * Atende UMA coisa de um pedido.
 *
 * **Sem chave de idempotência**, de propósito: doar duas vezes é uma ação
 * legítima — a chave recusaria a segunda doação deliberada. Vale mesmo agora que
 * há ouro no varal: quem recusa saldo curto é o livro-caixa, no servidor.
 */
export async function doarApetrecho(playerId, pedidoId, itemId, unidades) {
  const { data } = await api.post(
    `/api/player/${playerId}/clan/requests/${pedidoId}/donate`,
    unidades ? { itemId, unidades } : { itemId },
  );
  return data;
}

export async function cancelarPedido(playerId, pedidoId) {
  await api.delete(`/api/player/${playerId}/clan/requests/${pedidoId}`);
}

/* ---------------------------------------------------------------- negócio */

/** A tela `/business` inteira: armazém, funcionários e estoque. */
export async function negocio(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/business`);
  return {
    hasStorage: data.temArmazem,
    storage: data.armazem ? { maxCapacity: data.armazem.capacidade, tier: data.armazem.tier } : null,
    storageRequirements: data.requisitosDoArmazem
      ? { requiredLevel: data.requisitosDoArmazem.nivelExigido, cost: data.requisitosDoArmazem.custo }
      : null,
    // O próximo degrau do armazém: mais capacidade e mais alojamento.
    storageUpgrade: data.proximaMelhoria
      ? {
          tier: data.proximaMelhoria.tier,
          capacity: data.proximaMelhoria.capacidade,
          maxEmployees: data.proximaMelhoria.funcionarios,
          cost: data.proximaMelhoria.custo,
          requiredLevel: data.proximaMelhoria.nivelExigido,
        }
      : null,
    employees: {
      employees: (data.funcionarios || []).map(funcionarioDoNegocio),
      maxCount: data.maxFuncionarios,
    },
    // O estoque da EMPRESA (`business_fish`), e não o inventário do jogador — desde 25/09/2026.
    inventory: { totalFishByRarity: data.peixesPorRaridade || {} },
    // A folha por pagar: maior que zero, a próxima falta vende barco, casa ou licença.
    salaryDebt: data.dividaSalarial ?? 0,
  };
}

/**
 * O estoque da empresa e quanto ele vale — a prévia da venda, na forma que o
 * `FishMarketModal` já lia (`totalValue`, `totalFish`, `valueByMap`). A conta
 * é a mesma da venda, então o número mostrado é o número pago.
 */
export async function estoqueDaEmpresa(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/business/stock`);
  const valueByMap = {};
  for (const m of data.porMapa || []) {
    valueByMap[m.mapa] = { totalValue: m.valor, isFavorite: false, isLeastFavorite: false };
  }
  return {
    totalValue: data.valorTotal,
    baseValue: data.valorBase,
    totalFish: data.porRaridade || {},
    quantity: data.quantidade,
    valueByMap,
  };
}

/** Vende o estoque inteiro da empresa. O preço é do servidor. */
export async function venderEstoqueDaEmpresa(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/business/stock/sell`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { sold: data.vendidos, goldGained: data.goldGanho, gold: data.goldTotal };
}

/**
 * Os recibos da folha que o jogador ainda não fechou — o modal do login.
 * Cada um diz o que a folha daquele dia fez: pagou, virou dívida, vendeu bens,
 * ou a equipe foi embora.
 */
export async function recibosDaFolha(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/business/payroll/receipts`);
  return { receipts: data.recibos || [], debt: data.dividaAtual ?? 0 };
}

export async function fecharRecibosDaFolha(playerId) {
  await api.post(`/api/player/${playerId}/business/payroll/receipts/seen`, {});
}

/** Paga a dívida da folha com o ouro que houver (parcial vale). */
export async function pagarDividaDaFolha(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/business/payroll/pay-debt`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { paid: data.pago, debt: data.dividaRestante, gold: data.goldRestante };
}

export async function comprarArmazem(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/storage/purchase`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    capacity: data.capacidade,
    tier: data.tier,
    maxEmployees: data.funcionariosMax,
    goldSpent: data.goldGasto,
    gold: data.goldRestante,
  };
}

/** Sobe um tier do armazém: mais capacidade E mais alojamento, num degrau só. */
export async function melhorarArmazem(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/storage/upgrade`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    capacity: data.capacidade,
    tier: data.tier,
    maxEmployees: data.funcionariosMax,
    goldSpent: data.goldGasto,
    gold: data.goldRestante,
  };
}

/**
 * 7.17 — o livro-caixa do negócio (D26, proposta 5).
 *
 * Responde à pergunta que o sistema inteiro existe para responder e que hoje ninguém consegue
 * fazer: **"meu negócio dá lucro?"**.
 *
 * A resposta sai do `economy_ledger`, que é append-only desde a F2 e já guardava tudo de que ela
 * precisa — o D26 estimava que isto exigiria histórico novo no backend, e não exigia: só faltava a
 * rota de agregação.
 *
 * A receita é só a venda do estoque da empresa (`businessFishSale`, desde 25/09/2026). Antes era
 * toda venda de peixe do jogador, porque o peixe da equipe caía no inventário dele.
 */
export async function livroCaixa(playerId, periodo = 'dia') {
  const { data } = await api.get(`/api/player/${playerId}/business/ledger`, { params: { periodo } });
  return {
    periodo: data.periodo,
    rows: data.linhas,
    totals: data.totais,
    dailyPayroll: data.folhaDoDia,
    gold: data.ouroAtual,
    // O aviso ANTES de faltar (7.18). A tela decide o tom — e o D0 manda que seja suave.
    notEnoughGold: data.ouroInsuficiente,
  };
}

export async function folhaSalarial(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/employees/payroll`);
  return data.total;
}

/** Sorteia três candidatos. A lista vale 10 minutos e morre na contratação. */
export async function candidatos(playerId) {
  const { data } = await api.post(`/api/player/${playerId}/employees/candidates`, {});
  return { candidates: (data.candidatos || []).map(candidato), expiresAt: data.expiraEm };
}

/** Contrata pelo **id do candidato** — o servidor recusa qualquer outro campo. */
export async function contratar(playerId, candidatoId) {
  const { data } = await api.post(
    `/api/player/${playerId}/employees`,
    { candidatoId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return funcionarioDoNegocio({ ...data, mapaNome: null, jaPescouHoje: false, foto: 0, maxStamina: 100, catchRate: '0.5', criticalRate: '0.05', vara: null, isca: null });
}

export async function demitir(playerId, employeeId) {
  await api.delete(`/api/player/${playerId}/employees/${employeeId}`);
}

function equipamentoDoFuncionarioAdaptado(data) {
  return {
    rod: data.vara
      ? { id: data.vara.id, name: data.vara.nome, power: data.vara.poder, rarity: data.vara.raridade }
      : null,
    bait: data.isca
      ? { id: data.isca.id, name: data.isca.nome, power: data.isca.poder, quantity: data.isca.quantidade }
      : null,
  };
}

export async function equipamentoDoFuncionario(playerId, employeeId) {
  const { data } = await api.get(`/api/player/${playerId}/employees/${employeeId}/gear`);
  return equipamentoDoFuncionarioAdaptado(data);
}

/** Define a vara pelo id de **catálogo** (`null` desequipa). */
export async function definirVaraDoFuncionario(playerId, employeeId, rodId) {
  const { data } = await api.put(`/api/player/${playerId}/employees/${employeeId}/rod`, { rodId });
  return equipamentoDoFuncionarioAdaptado(data);
}

export async function entregarIsca(playerId, employeeId, baitId, quantidade) {
  const { data } = await api.post(`/api/player/${playerId}/employees/${employeeId}/bait`, {
    baitId,
    quantidade,
  });
  return equipamentoDoFuncionarioAdaptado(data);
}

export async function retirarIsca(playerId, employeeId) {
  const { data } = await api.delete(`/api/player/${playerId}/employees/${employeeId}/bait`);
  return equipamentoDoFuncionarioAdaptado(data);
}

export async function mapasDoFuncionario(playerId, employeeId) {
  const { data } = await api.get(`/api/player/${playerId}/employees/${employeeId}/maps`);
  return (data.itens || []).map((m) => ({
    id: m.id,
    name: m.nome,
    available: m.disponivel,
    currentlyAssigned: m.designado,
  }));
}

export async function designarMapaDoFuncionario(playerId, employeeId, mapId) {
  await api.put(`/api/player/${playerId}/employees/${employeeId}/map`, { mapId });
}

/** A jornada de pesca do dia, na forma que `DailyFishingModal` exibe. */
export async function pescaDiaria(playerId, employeeId) {
  const { data } = await api.post(
    `/api/player/${playerId}/employees/${employeeId}/daily-fishing`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    success: true,
    results: {
      totalCaught: data.capturas,
      totalAttempts: data.tentativas,
      fishCaught: data.porRaridade || {},
      failures: { lineBreaks: 0, fishEscapes: data.escaparam, noFishEvents: data.semPeixes },
      experienceGained: data.experienciaGanha,
      employeeLevel: data.nivelDoFuncionario,
      // A jornada parou porque o armazém encheu — melhorar o tier rende mais.
      storageFull: data.armazemCheio === true,
    },
  };
}

export async function upgradesDoFuncionario(playerId, employeeId) {
  const { data } = await api.get(`/api/player/${playerId}/employees/${employeeId}/upgrades`);
  const stats = {};
  const costs = {};
  for (const a of data.atributos || []) {
    stats[a.atributo] = a.nivel;
    costs[a.atributo] = a.custo;
  }
  return { stats, costs, gold: data.gold };
}

export async function aplicarUpgradeDoFuncionario(playerId, employeeId, atributo) {
  const { data } = await api.post(
    `/api/player/${playerId}/employees/${employeeId}/upgrades`,
    { atributo },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return {
    stat: data.atributo,
    level: data.nivel,
    cost: data.custoPago,
    gold: data.goldRestante,
    nextCost: data.proximoCusto,
  };
}

/* ---------------------------------------------------------------- eventos */

export async function eventosAtivos() {
  const { data } = await api.get('/api/events/active');
  return (data || []).map(eventoAtivo);
}

/** A soma dos efeitos por tipo, zeros incluídos — uma resposta, não uma por tipo. */
export async function efeitosDeEvento() {
  const { data } = await api.get('/api/events/effects');
  return data || {};
}

export async function ultimoEvento() {
  const { data } = await api.get('/api/events/last');
  return data ? eventoAtivo(data) : null;
}

export async function calendarioDeEventos(month, year) {
  const { data } = await api.get('/api/events/calendar', { params: { month, year } });
  return (data.itens || []).map((e) => ({
    id: e.id,
    name: e.nome,
    description: e.descricao || '',
    // O efeito veio para a agenda em 04/09/2026: sem ele o calendário só sabia
    // repetir o nome do evento em cada dia que ele cobre.
    effectType: e.tipoDeEfeito,
    effectNumber: e.numeroDoEfeito,
    typeEvent: e.tipo,
    startsAt: e.comecaEm,
    endsAt: e.terminaEm,
  }));
}

/* ---------------------------------------------------------------- mercado */

export async function mercado() {
  const { data } = await api.get('/api/market/today');
  return mercadoDoDia(data);
}

/**
 * Quanto vale o inventário — a prévia honesta da venda, com os mesmos bônus.
 * Na forma que `FishMarketModal` consome.
 */
export async function valorDoInventario(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/fish-value`);
  const valueByMap = {};
  for (const m of data.porMapa || []) {
    valueByMap[m.mapa] = {
      totalValue: m.valor,
      isFavorite: m.favorito,
      isLeastFavorite: m.menosFavorito,
    };
  }
  return {
    totalValue: data.valorTotal,
    baseValue: data.valorBase,
    totalFish: data.porRaridade || {},
    valueByMap,
    marketInfo: { favoriteMap: data.mapaFavorito, leastFavoriteMap: data.mapaMenosFavorito },
    charismaBonus: 1 + (data.bonusDeCarisma || 0) / 100,
  };
}

/* -------------------------------------------------------------- bestiário */

/** Todos os peixes, agrupados por mapa — a forma que `fishing.js` guarda. */
export async function todosOsPeixes() {
  const { data } = await api.get('/api/fishes');
  const porMapa = {};
  for (const peixe of data.itens || []) {
    for (const mapa of peixe.mapas || []) {
      if (!porMapa[mapa.id]) porMapa[mapa.id] = [];
      porMapa[mapa.id].push({
        id: peixe.id,
        name: peixe.nome,
        rarity: peixe.raridade,
        weight: Number(peixe.peso),
        value: peixe.valor,
        mythic: peixe.mitico,
      });
    }
  }
  return porMapa;
}

/* --------------------------------------------------------- perfil público */

export async function perfilPublicoDe(username) {
  const { data } = await api.get(`/api/players/${encodeURIComponent(username)}/profile`);
  return perfilPublico(data);
}

/* ------------------------------------------------------ descanso e rotina */

/** Liga/desliga o descanso em casa. Devolve o perfil com a energia em dia. */
export async function descansar(playerId, descansando) {
  const { data } = await api.post(`/api/player/${playerId}/rest`, { descansando });
  return perfilParaPlayerState(data, playerId);
}

/** Paga o imposto do dia. O valor é calculado no servidor. */
export async function cobrarImpostos(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/taxes/charge`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return { tax: data.imposto, gold: data.goldRestante };
}

/* ------------------------------------------------------------------ senha */

/**
 * Troca a senha. O servidor derruba **todas** as sessões — inclusive esta; o
 * chamador deve limpar a sessão local e mandar o jogador entrar de novo.
 */
export async function trocarSenha(currentPassword, newPassword) {
  await api.post('/api/auth/change-password', { currentPassword, newPassword });
  limparSessao();
}

/**
 * Esqueci a senha. Sempre 204 — o servidor não diz se o e-mail tem conta, e o
 * link de reset chega por e-mail (Resend), valendo 30 minutos.
 */
export async function esqueciSenha(email) {
  await api.post('/api/auth/forgot-password', { email });
}

/** Redefine com o código do e-mail. 400 = código inválido ou já usado. */
export async function redefinirSenha(code, password) {
  await api.post('/api/auth/reset-password', { code, password });
}

/** (Re)envia o link de confirmação de e-mail — sempre para a própria conta. */
export async function reenviarConfirmacao() {
  await api.post('/api/auth/send-email-confirmation', {});
}

export async function confirmarEmail(code) {
  await api.post('/api/auth/confirm-email', { code });
}

/* ----------------------------------------------------------- enciclopédia */

/**
 * A estante inteira: todo peixe do catálogo, descoberto ou não (D14).
 *
 * O `locale` vai na URL porque o texto mora no banco, por idioma — não em
 * `src/i18n/`. O **nome** é a exceção: continua vindo de `fishes.json`, e o
 * servidor manda `nome: null` para o não descoberto justamente para o cliente
 * saber que ali vai silhueta.
 *
 * Não descoberto vem com raridade e mapas de propósito: é o que faz o caderno
 * ser ferramenta de caça em vez de vitrine.
 */
export async function enciclopedia(playerId, locale) {
  const { data } = await api.get(`/api/player/${playerId}/encyclopedia`, {
    params: { locale },
  });
  return data;
}

/**
 * A entrada aberta de um peixe, com os três blocos.
 *
 * **404 é resposta esperada**, não erro: o peixe que o jogador ainda não pescou
 * não tem entrada. Quem chama só deve chamar para `descoberto`.
 */
export async function entradaDaEnciclopedia(playerId, fishingId, locale) {
  const { data } = await api.get(`/api/player/${playerId}/encyclopedia/${fishingId}`, {
    params: { locale },
  });
  return data;
}

/* ------------------------------------------------------ exploração (1.2) */

/**
 * As chamadas da exploração.
 *
 * **Sem adapter**, ao contrário das rotas antigas: o contrato da 1.2 já nasceu
 * em português e no formato que a tela consome — `requisitos`, `motivos` e
 * `tempoEstimadoMin` vêm prontos do servidor. Renomear campo aqui só criaria um
 * segundo vocabulário para a mesma coisa, que é exatamente o que os adapters
 * das rotas antigas existem para desfazer.
 *
 * **Nenhuma conta acontece neste arquivo.** Quem decide se a squad pode partir é
 * `avaliarRequisitos`, no domínio do servidor; a tela acende o indicador com o
 * que ele respondeu. Foi assim que `luck` ficou 7,5 no backend e 4,5 aqui.
 */

/** A visão geral — a única chamada que a tela inicial faz (D4: não há socket). */
export async function exploracao(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration`);
  return data;
}

export async function rolarCandidatos(playerId, guilda, quantidade = 3) {
  const { data } = await api.post(`/api/player/${playerId}/exploration/recruitment/roll`, {
    guilda,
    quantidade,
  });
  return data;
}

export async function contratarIntegrante(playerId, candidatoId) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/recruitment/hire`,
    { candidatoId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function integrantes(playerId, filtros = {}) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/explorers`, {
    params: filtros,
  });
  return data;
}

export async function integrante(playerId, explorerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/explorers/${explorerId}`);
  return data;
}

/** `explorerItemId: null` desequipa e devolve a unidade ao estoque. */
/**
 * Equipar. O slot é o do item; só entra no corpo ao desequipar (`explorerItemId`
 * nulo), para dizer qual slot esvaziar — sem ele, esvazia os três.
 */
export async function equiparIntegrante(playerId, explorerId, explorerItemId, slot) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/explorers/${explorerId}/equip`,
    slot ? { explorerItemId, slot } : { explorerItemId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** Usa um consumível do estoque no integrante. */
export async function usarItemNoIntegrante(playerId, explorerId, explorerItemId) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/explorers/${explorerId}/use`,
    { explorerItemId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/**
 * Distribui pontos de nível num stat. Sem chave de idempotência de propósito:
 * dois cliques no "+" são dois pontos, e a mesma chave faria o segundo devolver
 * a resposta do primeiro. Quem impede gastar demais é o servidor.
 */
export async function distribuirPontosDoIntegrante(playerId, explorerId, stat, quantidade = 1) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/explorers/${explorerId}/points`,
    { stat, quantidade },
  );
  return data;
}

export async function dispensarIntegrante(playerId, explorerId) {
  await api.delete(`/api/player/${playerId}/exploration/explorers/${explorerId}`);
}

export async function squadsDoJogador(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/squads`);
  return data;
}

export async function criarSquad(playerId, nome) {
  const { data } = await api.post(`/api/player/${playerId}/exploration/squads`, { nome });
  return data;
}

export async function editarSquad(playerId, squadId, dados) {
  const { data } = await api.patch(`/api/player/${playerId}/exploration/squads/${squadId}`, dados);
  return data;
}

/**
 * A composição inteira, de uma vez (D10).
 *
 * Não há `assign`/`remove` um a um: dois cliques rápidos com validação no meio
 * deixam a squad num estado que nenhuma das duas chamadas queria.
 */
export async function definirMembrosDaSquad(playerId, squadId, explorerIds) {
  const { data } = await api.put(
    `/api/player/${playerId}/exploration/squads/${squadId}/members`,
    { explorerIds },
  );
  return data;
}

export async function apagarSquad(playerId, squadId) {
  await api.delete(`/api/player/${playerId}/exploration/squads/${squadId}`);
}

/** Cada bioma com `elegivel`, `motivos` e o tempo que o servidor vai usar. */
export async function biomasDaSquad(playerId, squadId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/squads/${squadId}/biomes`);
  return data;
}

export async function agendarExpedicao(playerId, squadId, biomeId) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/expeditions`,
    { squadId, biomeId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** O segundo resgate devolve o mesmo resultado, e não um erro. */
export async function resgatarExpedicao(playerId, expeditionId) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/expeditions/${expeditionId}/claim`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function materiaisDoJogador(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/materials`);
  return data;
}

/**
 * Vender material do armazém. O preço é do servidor: aqui vai só o que e quanto.
 *
 * A chave idempotente é **por chamada** e não por material: vender a mesma pilha
 * duas vezes de propósito é legítimo, e uma chave fixa recusaria a segunda como
 * duplicata — a mesma armadilha da compra de item.
 */
export async function venderMaterial(playerId, materialId, quantidade) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/materials/sell`,
    { materialId, quantidade },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function estoqueDeItensDeIntegrante(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/items`);
  return data;
}

/** As receitas da oficina, já avaliadas para o jogador (o que tem, quantas vezes dá). */
export async function receitasDaOficina(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/exploration/recipes`);
  return data;
}

export async function craftar(playerId, recipeId, vezes = 1) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/craft`,
    { recipeId, vezes },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function comprarAeronave(playerId, aircraftModelId) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/aircraft/buy`,
    { aircraftModelId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

export async function comprarItemDeIntegrante(playerId, explorerItemId, quantidade = 1) {
  const { data } = await api.post(
    `/api/player/${playerId}/exploration/shop/buy`,
    { explorerItemId, quantidade },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/**
 * O catálogo da exploração. `oQue` é `aeronaves`, `biomas`, `itens` ou
 * `materiais`; é igual para todo mundo e o servidor o cacheia por uma hora.
 */
export async function catalogoDeExploracao(oQue) {
  const { data } = await api.get(`/api/catalogo/exploracao/${oQue}`);
  return data;
}

/* ------------------------------------------------------------------ outros */

export async function anuncios() {
  const { data } = await api.get('/api/announcement/desc');
  return data;
}

export { api, definirAccessToken, limparSessao, obterAccessToken, temSessao };
export { restaurarSessao, esquecerRestauracao } from './restaurar';

/* ------------------------------------------------- a oficina do pescador */

/**
 * O baú de apetrechos (anzol, boia, caixa): toda unidade, os três equipados e
 * a soma que a pescaria aplica. Uma chamada, como o baú de vara/barco/casa.
 */
export async function apetrechos(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/gear/fishing`);
  return data;
}

/** Equipar uma unidade. `playerFishingGearId` é a instância, não o catálogo. */
export async function equiparApetrecho(playerId, playerFishingGearId) {
  const { data } = await api.post(
    `/api/player/${playerId}/gear/fishing/equip`,
    { playerFishingGearId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** Desequipar um slot: `anzol`, `boia` ou `caixa`. */
export async function desequiparApetrecho(playerId, slot) {
  const { data } = await api.post(
    `/api/player/${playerId}/gear/fishing/unequip`,
    { slot },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** As poções no estoque do pescador. */
export async function pocoes(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/potions`);
  return data;
}

/** Os efeitos valendo agora, com `restaSegundos` contado no relógio do servidor. */
export async function efeitosDePocao(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/potions/effects`);
  return data;
}

export async function beberPocao(playerId, potionId) {
  const { data } = await api.post(
    `/api/player/${playerId}/potions/use`,
    { potionId },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/* ----------------------------------------------------- habilidades (1.3) */

/**
 * A visão das três árvores: pontos, cada habilidade com nível, se libera e por
 * que não, o efeito já escrito, e o reset. Nada é derivado no cliente.
 */
export async function habilidades(playerId) {
  const { data } = await api.get(`/api/player/${playerId}/skills`);
  return data;
}

/** Um ponto em uma habilidade. Devolve a visão nova. Idempotente. */
export async function alocarHabilidade(playerId, skillId) {
  const { data } = await api.post(
    `/api/player/${playerId}/skills/${skillId}`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** Reset total das árvores (pago por ponto, com cooldown). Devolve a visão nova. Idempotente. */
export async function resetarHabilidades(playerId) {
  const { data } = await api.post(
    `/api/player/${playerId}/skills/reset`,
    {},
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// O mercado entre jogadores
// ---------------------------------------------------------------------------

/**
 * A vitrine, paginada por cursor.
 *
 * **Fica sob `:playerId` mesmo sendo global**, e não é descuido: cada anúncio
 * vem com `meu`, que a tela usa para trocar "comprar" por "cancelar", e esse
 * campo exige o jogador de quem olha. A rota sem `:playerId` gastaria uma query
 * a mais por página só para calcular um booleano — o porquê completo está no
 * `mercado.controller.ts`.
 *
 * `ordem: 'preco'`/`'precoDesc'` e a faixa `precoMin`/`precoMax` **exigem**
 * `moeda`: comparar 30 Parallelium com 12 000 de ouro não quer dizer nada, e o
 * servidor recusa com 400 em vez de devolver uma lista que parece ordenada e
 * não está. O preço é POR UNIDADE (`0054`).
 *
 * Paginada por NÚMERO (`pagina`, `porPagina`), e não por cursor como o resto da
 * API: a resposta traz `total` e `totalDePaginas`, que é o que o controle de
 * páginas precisa. O porquê está em `paginaDoMercadoSchema`, no contrato.
 */
export async function vitrineDoMercado(playerId, filtros = {}) {
  const { data } = await api.get(`/api/player/${playerId}/market/listings`, { params: filtros });
  return data;
}

/** Um anúncio só — a tela de detalhe e o link que se manda no chat do clã. */
export async function anuncioDoMercado(playerId, anuncioId) {
  const { data } = await api.get(`/api/player/${playerId}/market/listings/${anuncioId}`);
  return data;
}

/**
 * A prévia da taxa antes de confirmar.
 *
 * O cliente sabe calcular isto (`previaDeAnuncio`, do domínio), e chama de todo
 * jeito por uma razão que ele não tem como resolver sozinho: **quantos anúncios
 * abertos o jogador já tem**. Sem esse número a tela ofereceria anunciar para
 * depois o servidor recusar por teto.
 *
 * `preco` é por unidade e `quantidade` é o lote: a taxa sai do lote inteiro.
 */
export async function previaDeAnuncio(playerId, { preco, quantidade = 1, moeda }) {
  const { data } = await api.post(`/api/player/${playerId}/market/listings/preview`, {
    preco,
    quantidade,
    moeda,
  });
  return data;
}

/**
 * Anuncia. `tipo` decide o que `alvoId` significa:
 *
 * | tipo | `alvoId` é |
 * |---|---|
 * | `explorer` / `employee` | a INSTÂNCIA (`explorers.id`, `employees.id`) |
 * | `material` / `explorerItem` | o CATÁLOGO — é o que o jogador vê no armazém |
 *
 * `preco` é POR UNIDADE desde a `0054`; o lote vale `preco * quantidade`.
 *
 * Idempotente porque o duplo clique custa duas taxas e dois anúncios, e o
 * segundo consome estoque de verdade.
 */
export async function anunciarNoMercado(playerId, { tipo, alvoId, quantidade, moeda, preco }) {
  const { data } = await api.post(
    `/api/player/${playerId}/market/listings`,
    { tipo, alvoId, ...(quantidade === undefined ? {} : { quantidade }), moeda, preco },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/**
 * Compra `quantidade` unidades do anúncio — uma, parte do lote, ou o lote todo
 * (`0054`). Idempotente, e aqui isso importa mais que em qualquer outra rota:
 * sem a chave, o segundo clique não daria erro — ele compraria **mais** unidades
 * do mesmo lote, que é a pior versão do bug.
 */
export async function comprarNoMercado(playerId, anuncioId, quantidade = 1) {
  const { data } = await api.post(
    `/api/player/${playerId}/market/listings/${anuncioId}/buy`,
    { quantidade },
    { headers: { 'Idempotency-Key': chaveIdempotente() } },
  );
  return data;
}

/** Cancela. Devolve o que estava guardado; a taxa de anúncio não volta. */
export async function cancelarAnuncio(playerId, anuncioId) {
  await api.delete(`/api/player/${playerId}/market/listings/${anuncioId}`);
}

/**
 * Meus anúncios: abertos e histórico, na mesma lista, com os abertos primeiro.
 * `params`: `status` (`todos`, `open`, `sold`, `cancelled`, `expired`),
 * `pagina`, `porPagina`.
 */
export async function meusAnuncios(playerId, params = {}) {
  const { data } = await api.get(`/api/player/${playerId}/market/my-listings`, { params });
  return data;
}

/**
 * O que eu comprei — uma linha por COMPRA (`0054`): voltar ao mesmo lote são
 * duas linhas. O retrato do anúncio é a única prova do que era.
 * `params`: `pagina`, `porPagina`. A resposta traz `compras`, e não `anuncios`.
 */
export async function minhasCompras(playerId, params = {}) {
  const { data } = await api.get(`/api/player/${playerId}/market/purchases`, { params });
  return data;
}
